import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  FileText,
  Settings,
  Calendar,
  Users,
  CheckCircle,
  RefreshCw,
  Target,
  UserCheck,
  Mail,
  Building2
} from 'lucide-react';

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, steps }) => {
  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const Icon = step.icon;
        
        return (
          <div key={index} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                ${isCompleted ? 'bg-green-500 text-white' : 
                  isCurrent ? 'bg-webtech-blue text-white ring-4 ring-webtech-blue/20' : 
                  'bg-slate-100 text-slate-400'}
              `}>
                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-2 font-medium text-center ${isCurrent ? 'text-webtech-blue' : 'text-slate-500'}`}>
                {step.label}
              </span>
            </div>
            {index < totalSteps - 1 && (
              <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${
                isCompleted ? 'bg-green-500' : 'bg-slate-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function CampaignWizard({ 
  open, 
  onOpenChange, 
  companyId, 
  userId, 
  departments = [],
  employees = [],
  onSuccess 
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'annual',
    mode: '180',
    startDate: '',
    endDate: '',
    deadlineObjectives: '',
    deadlineEvaluation: '',
    enableSelfEvaluation: true,
    enableAutoReminders: true,
    targetDepartments: [],
    targetEmployeeIds: []
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setFormData({
        name: '',
        description: '',
        type: 'annual',
        mode: '180',
        startDate: '',
        endDate: '',
        deadlineObjectives: '',
        deadlineEvaluation: '',
        enableSelfEvaluation: true,
        enableAutoReminders: true,
        targetDepartments: [],
        targetEmployeeIds: []
      });
    }
  }, [open]);

  const steps = [
    { label: 'Informations', icon: FileText },
    { label: 'Mode', icon: Settings },
    { label: 'Dates', icon: Calendar },
    { label: 'Ciblage', icon: Users },
    { label: 'Récapitulatif', icon: CheckCircle }
  ];

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.name.trim()) {
          toast.error('Veuillez saisir un nom pour la campagne');
          return false;
        }
        return true;
      case 1:
        return true;
      case 2:
        if (!formData.startDate || !formData.endDate) {
          toast.error('Veuillez remplir les dates d\'ouverture et de clôture');
          return false;
        }
        if (new Date(formData.startDate) > new Date(formData.endDate)) {
          toast.error('La date d\'ouverture doit être avant la date de clôture');
          return false;
        }
        return true;
      case 3:
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post(`/campaigns?company_id=${companyId}&created_by=${userId}`, formData);
      toast.success('🎉 Campagne créée avec succès !');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const toggleDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      targetDepartments: prev.targetDepartments.includes(deptId)
        ? prev.targetDepartments.filter(id => id !== deptId)
        : [...prev.targetDepartments, deptId]
    }));
  };

  const getTypeLabel = (type) => {
    const types = { annual: 'Annuelle', semester: 'Semestrielle', quarterly: 'Trimestrielle' };
    return types[type] || type;
  };

  const getModeLabel = (mode) => {
    return mode === '360' ? '360° (Manager + Employé + Pairs)' : '180° (Manager + Employé)';
  };

  // Step 1: Basic Information
  const renderStep1 = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-webtech-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-webtech-blue" />
        </div>
        <h3 className="text-lg font-semibold">Informations de base</h3>
        <p className="text-slate-500 text-sm">Donnez un nom et une description à votre campagne</p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nom de la campagne *</Label>
          <Input
            id="name"
            placeholder="Ex: Évaluation Annuelle 2025"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="text-lg"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="type">Type de campagne</Label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'annual', label: 'Annuelle', icon: '📅' },
              { value: 'semester', label: 'Semestrielle', icon: '📆' },
              { value: 'quarterly', label: 'Trimestrielle', icon: '🗓️' }
            ].map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData({ ...formData, type: type.value })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.type === type.value
                    ? 'border-webtech-blue bg-webtech-blue/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className="text-2xl block mb-1">{type.icon}</span>
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="description">Description (optionnel)</Label>
          <Textarea
            id="description"
            placeholder="Décrivez les objectifs de cette campagne..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />
        </div>
      </div>
    </div>
  );

  // Step 2: Evaluation Mode
  const renderStep2 = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Settings className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold">Mode d'évaluation</h3>
        <p className="text-slate-500 text-sm">Choisissez comment les évaluations seront conduites</p>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, mode: '180' })}
            className={`p-6 rounded-xl border-2 transition-all text-left ${
              formData.mode === '180'
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <Badge className="bg-blue-100 text-blue-700 mb-3">180°</Badge>
            <h4 className="font-semibold mb-2">Évaluation classique</h4>
            <p className="text-sm text-slate-500">
              Le manager évalue l'employé + auto-évaluation
            </p>
            <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
              <UserCheck className="w-4 h-4" />
              Manager → Employé
            </div>
          </button>
          
          <button
            type="button"
            onClick={() => setFormData({ ...formData, mode: '360' })}
            className={`p-6 rounded-xl border-2 transition-all text-left ${
              formData.mode === '360'
                ? 'border-purple-500 bg-purple-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <Badge className="bg-purple-100 text-purple-700 mb-3">360°</Badge>
            <h4 className="font-semibold mb-2">Évaluation complète</h4>
            <p className="text-sm text-slate-500">
              Manager + auto-évaluation + pairs
            </p>
            <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
              <Users className="w-4 h-4" />
              Vision à 360 degrés
            </div>
          </button>
        </div>
        
        <div className="bg-slate-50 rounded-xl p-4 space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Options
          </h4>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Auto-évaluation</p>
              <p className="text-xs text-slate-500">Les employés s'évaluent eux-mêmes</p>
            </div>
            <Switch
              checked={formData.enableSelfEvaluation}
              onCheckedChange={(checked) => setFormData({ ...formData, enableSelfEvaluation: checked })}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Relances automatiques</p>
              <p className="text-xs text-slate-500">Rappels par email avant les deadlines</p>
            </div>
            <Switch
              checked={formData.enableAutoReminders}
              onCheckedChange={(checked) => setFormData({ ...formData, enableAutoReminders: checked })}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: Dates
  const renderStep3 = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold">Planification</h3>
        <p className="text-slate-500 text-sm">Définissez les dates clés de la campagne</p>
      </div>
      
      <div className="space-y-6">
        <div className="bg-blue-50 rounded-xl p-4">
          <h4 className="font-medium text-blue-800 mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Période de la campagne *
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-blue-700">Date d'ouverture</Label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="bg-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-blue-700">Date de clôture</Label>
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="bg-white"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-orange-50 rounded-xl p-4">
          <h4 className="font-medium text-orange-800 mb-4 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Dates limites (optionnel)
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-orange-700">Limite objectifs</Label>
              <Input
                type="date"
                value={formData.deadlineObjectives}
                onChange={(e) => setFormData({ ...formData, deadlineObjectives: e.target.value })}
                className="bg-white"
              />
              <p className="text-xs text-orange-600">Date limite pour définir les objectifs</p>
            </div>
            <div className="space-y-2">
              <Label className="text-orange-700">Limite évaluations</Label>
              <Input
                type="date"
                value={formData.deadlineEvaluation}
                onChange={(e) => setFormData({ ...formData, deadlineEvaluation: e.target.value })}
                className="bg-white"
              />
              <p className="text-xs text-orange-600">Date limite pour compléter les évaluations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 4: Targeting
  const renderStep4 = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-orange-600" />
        </div>
        <h3 className="text-lg font-semibold">Ciblage</h3>
        <p className="text-slate-500 text-sm">Sélectionnez les départements concernés</p>
      </div>
      
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">Départements ciblés</h4>
            {formData.targetDepartments.length > 0 && (
              <Badge className="bg-webtech-blue text-white">
                {formData.targetDepartments.length} sélectionné(s)
              </Badge>
            )}
          </div>
          
          {departments.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Aucun département configuré</p>
              <p className="text-xs">Tous les employés seront inclus</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-3">
                Cliquez pour sélectionner. Laissez vide pour tous les employés.
              </p>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                {departments.map(dept => (
                  <button
                    key={dept.id}
                    type="button"
                    onClick={() => toggleDepartment(dept.id)}
                    className={`px-4 py-2 rounded-full border transition-all ${
                      formData.targetDepartments.includes(dept.id)
                        ? 'bg-webtech-blue text-white border-webtech-blue'
                        : 'bg-white border-slate-200 hover:border-webtech-blue hover:text-webtech-blue'
                    }`}
                  >
                    {formData.targetDepartments.includes(dept.id) && (
                      <Check className="w-4 h-4 inline mr-1" />
                    )}
                    {dept.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        
        <div className="bg-green-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800">Notifications automatiques</p>
              <p className="text-sm text-green-600">
                {formData.enableAutoReminders 
                  ? 'Les employés seront notifiés par email à chaque étape'
                  : 'Les notifications automatiques sont désactivées'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 5: Summary
  const renderStep5 = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold">Récapitulatif</h3>
        <p className="text-slate-500 text-sm">Vérifiez les informations avant de créer</p>
      </div>
      
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-5 h-5 text-slate-400" />
              <span className="font-medium">Informations</span>
            </div>
            <div className="pl-8 space-y-1">
              <p><span className="text-slate-500">Nom:</span> <strong>{formData.name}</strong></p>
              <p><span className="text-slate-500">Type:</span> {getTypeLabel(formData.type)}</p>
              {formData.description && (
                <p className="text-sm text-slate-500">{formData.description}</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Settings className="w-5 h-5 text-slate-400" />
              <span className="font-medium">Mode d'évaluation</span>
            </div>
            <div className="pl-8 space-y-2">
              <div className="flex items-center gap-2">
                <Badge className={formData.mode === '360' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                  {formData.mode}°
                </Badge>
                <span>{getModeLabel(formData.mode)}</span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className={formData.enableSelfEvaluation ? 'text-green-600' : 'text-slate-400'}>
                  {formData.enableSelfEvaluation ? '✓' : '✗'} Auto-évaluation
                </span>
                <span className={formData.enableAutoReminders ? 'text-green-600' : 'text-slate-400'}>
                  {formData.enableAutoReminders ? '✓' : '✗'} Relances auto
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span className="font-medium">Dates</span>
            </div>
            <div className="pl-8 grid grid-cols-2 gap-2 text-sm">
              <p><span className="text-slate-500">Ouverture:</span> {formData.startDate ? new Date(formData.startDate).toLocaleDateString('fr-FR') : '-'}</p>
              <p><span className="text-slate-500">Clôture:</span> {formData.endDate ? new Date(formData.endDate).toLocaleDateString('fr-FR') : '-'}</p>
              <p><span className="text-slate-500">Limite objectifs:</span> {formData.deadlineObjectives ? new Date(formData.deadlineObjectives).toLocaleDateString('fr-FR') : '-'}</p>
              <p><span className="text-slate-500">Limite évaluations:</span> {formData.deadlineEvaluation ? new Date(formData.deadlineEvaluation).toLocaleDateString('fr-FR') : '-'}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5 text-slate-400" />
              <span className="font-medium">Ciblage</span>
            </div>
            <div className="pl-8">
              {formData.targetDepartments.length === 0 ? (
                <p className="text-sm text-slate-500">Tous les employés de l'entreprise</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {formData.targetDepartments.map(deptId => {
                    const dept = departments.find(d => d.id === deptId);
                    return dept ? (
                      <Badge key={deptId} variant="outline">{dept.name}</Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      case 3: return renderStep4();
      case 4: return renderStep5();
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Créer une campagne d'évaluation</DialogTitle>
          <DialogDescription>
            Étape {currentStep + 1} sur {steps.length}
          </DialogDescription>
        </DialogHeader>
        
        <StepIndicator 
          currentStep={currentStep} 
          totalSteps={steps.length}
          steps={steps}
        />
        
        <div className="min-h-[400px]">
          {renderCurrentStep()}
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                className="bg-webtech-blue hover:bg-webtech-blue-hover"
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-green-600 hover:bg-green-700"
              >
                {creating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Créer la campagne
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
